import { useCallback, useState } from 'react'
import { useGetMeetingTemplateByIdQuery, useUpdateMeetingTemplateMutation } from '../../../store/ai/meetingTemplate'
import { parseMetadata } from '../../../common/util'

export interface MeetingTemplateMetadata {
  isAgendaEnabled?: boolean
}

export const useUpdateMeetingTemplateMetadata = (accountId: string, meetingTemplateId: string | null) => {
  const { refetch } = useGetMeetingTemplateByIdQuery({ accountId, meetingTemplateId: meetingTemplateId || '' }, { skip: !meetingTemplateId })
  const [updateMeetingTemplate, { isLoading: isUpdating }] = useUpdateMeetingTemplateMutation()
  const [error, setError] = useState<string | null>(null)

  const updateMetadata = useCallback(
    async (metadataUpdate: Partial<MeetingTemplateMetadata>) => {
      if (!meetingTemplateId) {
        setError('Meeting template ID is required')
        return { success: false, error: 'Meeting template ID is required' }
      }

      setError(null)

      const { data: freshData } = await refetch()

      if (!freshData?.data) {
        const errorMsg = 'Failed to fetch meeting template'
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }

      const currentMetadata = parseMetadata(freshData.data.metadata)

      const updatedMetadata: MeetingTemplateMetadata = {
        ...currentMetadata,
        ...metadataUpdate,
      }

      const metadataString = JSON.stringify(updatedMetadata)

      const result = await updateMeetingTemplate({
        accountId,
        meetingTemplateId,
        payload: {
          metadata: metadataString,
        },
      })

      if ('error' in result && result.error) {
        const errorMsg = 'message' in result.error && typeof result.error.message === 'string' ? result.error.message : 'Failed to update metadata'
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }

      return { success: true, data: updatedMetadata }
    },
    [accountId, meetingTemplateId, refetch, updateMeetingTemplate],
  )

  return {
    updateMetadata,
    isLoading: isUpdating,
    error,
  }
}
